import React, { Fragment, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Form from './Form';
import FrontCanvas from './FrontCanvas';
import './BrochureEditor.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as selectors from './BrochureEditor.selectors';
import * as actions from './BrochureEditor.actions';
import { stringifyEqualityCheck } from './../../utils';
import * as loginSelectors from './../Login/Login.selectors';
import { Link } from 'react-router-dom';

const BrochureEditor = (props) => {
    /* State to props */
    const templateCode = useSelector(selectors.getTemplateCode);
    const quantity = useSelector(selectors.getQuantity);
    const brochure = useSelector(selectors.getBrochure, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const addingToCart = useSelector(selectors.getAddingToCart);
    const isFavorite = useSelector(selectors.getIsFavorite);
    const checkingFavorite = useSelector(selectors.getCheckingFavorite);
    const notes = useSelector(selectors.getNotes);
    const savingWork = useSelector(selectors.getSavingWork);
    const action = useSelector(selectors.getAction);
    const type = useSelector(selectors.getType);
    const clinicName = useSelector(selectors.getClinicName);
    const address = useSelector(selectors.getAddress);
    const city = useSelector(selectors.getCity);
    const zip = useSelector(selectors.getZip);
    const main = useSelector(selectors.getMain);
    const fax = useSelector(selectors.getFax);
    const cxmlData = useSelector(loginSelectors.getCxmlData, stringifyEqualityCheck);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchBrochure = useCallback((id, departmentId) => dispatch(actions.fetchBrochure(id, departmentId)), [dispatch]);
    const fetchDepartment = useCallback((id) => dispatch(actions.fetchDepartment(id)), [dispatch]);
    const setTemplateCode = useCallback((value) => dispatch(actions.setTemplateCode(value)), [dispatch]);
    const setQuantity = useCallback((value) => dispatch(actions.setQuantity(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const addToCart = useCallback((orderType, history, type) => dispatch(actions.addToCart(orderType, history, type)), [dispatch]);
    const setNotes = useCallback((value) => dispatch(actions.setNotes(value)), [dispatch]);
    const addFavorite = useCallback((history) => dispatch(actions.addFavorite(history)), [dispatch]);
    const checkFavorite = useCallback((templateCode) => dispatch(actions.checkFavorite(templateCode)), [dispatch]);
    const setAction = useCallback((value) => dispatch(actions.setAction(value)), [dispatch]);
    const setType = useCallback((value) => dispatch(actions.setType(value)), [dispatch]);
    const setClinicName = useCallback((value) => dispatch(actions.setClinicName(value)), [dispatch]);
    const setAddress = useCallback((value) => dispatch(actions.setAddress(value)), [dispatch]);
    const setCity = useCallback((value) => dispatch(actions.setCity(value)), [dispatch]);
    const setZip = useCallback((value) => dispatch(actions.setZip(value)), [dispatch]);
    const setMain = useCallback((value) => dispatch(actions.setMain(value)), [dispatch]);
    const setFax = useCallback((value) => dispatch(actions.setFax(value)), [dispatch]);
    const loadVariables = useCallback(() => dispatch(actions.loadVariables()), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        resetState();
        setTemplateCode(props.match.params.id);
        checkFavorite(props.match.params.id);
        fetchBrochure(props.match.params.id, props.match.params.departmentId);
        fetchDepartment(props.match.params.departmentId);

        if (props.match.params.action === 'edit') {
            loadVariables();
            setAction('edit');
        } else if (props.match.params.action === 'reorder') {
            loadVariables();
            setAction('reorder');
        }

        return () => {
            resetState();
        };
    }, [setTemplateCode, resetState, fetchBrochure, fetchDepartment, checkFavorite, loadVariables, setAction,
        props.match.params.id, props.match.params.departmentId, props.match.params.action]);

    let editorContainerWidth = 985;
    let leftContainerBasis = 500;
    let rightContainerBasis = 450;
    if (brochure && parseInt(brochure.id) === 12) {
        editorContainerWidth = 1100;
        leftContainerBasis = 380;
        rightContainerBasis = 690;
    }

    let content = <div className="text-center"><i className="fas fa-circle-notch fa-spin" /> Loading data...</div>;
    if (!fetching) {
        content = (
            <Fragment>
                <div className="fonts-helper">
                    <div className="LinotypeUniversLTPro65Bold">a</div>
                    <div className="LinotypeUniversLTPro55Roman">a</div>
                </div>
                <div className="steps-content">
                    <div className="left-side-container" style={{ flexBasis: leftContainerBasis }}>
                        <Form
                            quantity={quantity}
                            cxmlData={cxmlData}
                            templateCode={templateCode}
                            brochure={brochure}
                            fetching={fetching}
                            addingToCart={addingToCart}
                            notes={notes}
                            history={props.history}
                            setQuantity={setQuantity}
                            addToCart={addToCart}
                            setNotes={setNotes}
                            isFavorite={isFavorite}
                            addFavorite={addFavorite}
                            checkingFavorite={checkingFavorite}
                            savingWork={savingWork}
                            action={action}
                            type={type}
                            setType={setType}
                            clinicName={clinicName}
                            address={address}
                            city={city}
                            zip={zip}
                            main={main}
                            fax={fax}
                            user={user}
                            setClinicName={setClinicName}
                            setAddress={setAddress}
                            setCity={setCity}
                            setZip={setZip}
                            setMain={setMain}
                            setFax={setFax}
                        />
                    </div>
                    <div className="right-side-container" style={{ flexBasis: rightContainerBasis }}>
                        <FrontCanvas
                            templateCode={templateCode}
                            quantity={quantity}
                            templateId={props.match.params.id}
                            history={props.history}
                            brochure={brochure}
                            fetching={fetching}
                            addingToCart={addingToCart}
                            notes={notes}
                            cxmlData={cxmlData}
                            addToCart={addToCart}
                            action={action}
                            type={type}
                        />
                    </div>
                </div>
            </Fragment>
        );
    }

    let breadcrumbsContainer = null;
    if (brochure != null) {
        let categoryName = 'Marketing Collateral'
        if ([3, 4, 5, 7, 9, 10, 13, 14].includes(parseInt(brochure.id))) {
            categoryName = 'Legal Notices'
        }

        let breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to={'/department/16/' + brochure.breadcrumbs[0].id}>{categoryName}</Link>,
            ' / ',
            brochure.name,
        ];

        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-paint-brush"></i> Editor</Fragment>}
            step={3}
            history={props.history}
        >
            <Helmet><title>Editor - UT Physicians</title></Helmet>
            <div className="brochure-editor-container" style={{ width: editorContainerWidth }}>
                {breadcrumbsContainer}
                {content}
            </div>
        </Layout>
    );
};

export default BrochureEditor;
